import { useRef, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { ImagePlus, Trash2, Pencil, Star, X, Crosshair } from 'lucide-react'
import { adminApi, websiteImageUrl, type WebsiteImageDto } from '@/api/admin'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { categoryErrorMessage } from './categoryErrors'

const MAX_IMAGES = 8
const MAX_ALT = 200

/** A picture cropped to fit its box, keeping the chosen focus point in view. */
export function FocusedImage({ image, width, className }: { image: WebsiteImageDto; width: number; className?: string }) {
  return (
    <img
      src={websiteImageUrl(image.id, width)}
      alt={image.altText}
      className={className}
      style={{ objectFit: 'cover', objectPosition: `${image.focusX}% ${image.focusY}%` }}
    />
  )
}

/** Choose a file, describe it, and send it. The description is required because it is read out by screen readers. */
export function ImageUploadForm({
  onUpload, pending, error, onCancel,
}: {
  onUpload: (file: File, alt: string) => void
  pending: boolean
  error: string | null
  onCancel?: () => void
}) {
  const [file, setFile] = useState<File | null>(null)
  const [alt, setAlt] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  return (
    <div className="space-y-2 rounded-lg border border-dashed border-gray-300 bg-gray-50 p-3">
      <div className="flex flex-wrap items-center gap-2">
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          className="text-sm text-gray-600 file:mr-3 file:rounded-md file:border-0 file:bg-indigo-50 file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-indigo-700 hover:file:bg-indigo-100"
          onChange={e => setFile(e.target.files?.[0] ?? null)}
          aria-label="Picture file"
        />
        <span className="text-xs text-gray-400">JPEG, PNG or WebP, up to 10 MB</span>
      </div>
      <div className="space-y-1">
        <Input
          value={alt}
          maxLength={MAX_ALT}
          onChange={e => setAlt(e.target.value)}
          placeholder="Describe the picture, e.g. Learners practising CPR on mannequins"
          aria-label="Picture description"
        />
        <p className="text-xs text-gray-500">Read out to people using screen readers, and used by search engines.</p>
      </div>
      {error && <p className="text-xs text-red-600">{error}</p>}
      <div className="flex gap-2">
        <Button type="button" size="sm" disabled={!file || !alt.trim() || pending}
          onClick={() => file && onUpload(file, alt.trim())}>
          {pending ? 'Uploading…' : 'Upload'}
        </Button>
        {onCancel && <Button type="button" size="sm" variant="outline" onClick={onCancel}>Cancel</Button>}
      </div>
    </div>
  )
}

/** Edit a picture's description and click the picture to say which part matters most. */
export function ImageEditor({ image, onClose }: { image: WebsiteImageDto; onClose: () => void }) {
  const queryClient = useQueryClient()
  const [alt, setAlt] = useState(image.altText)
  const [focus, setFocus] = useState({ x: image.focusX, y: image.focusY })
  const [error, setError] = useState<string | null>(null)

  const save = useMutation({
    mutationFn: () => adminApi.updateWebsiteImage(image.id, { altText: alt.trim(), focusX: focus.x, focusY: focus.y }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['course-website'] })
      await queryClient.invalidateQueries({ queryKey: ['course-categories'] })
      onClose()
    },
    onError: err => setError(categoryErrorMessage(err)),
  })

  const pick = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect()
    const clamp = (n: number) => Math.min(100, Math.max(0, Math.round(n)))
    setFocus({ x: clamp(((e.clientX - rect.left) / rect.width) * 100), y: clamp(((e.clientY - rect.top) / rect.height) * 100) })
  }

  return (
    <div className="space-y-3 rounded-lg border border-gray-200 bg-white p-3">
      <div className="flex items-start justify-between gap-3">
        <p className="text-sm font-medium text-gray-900">Edit picture</p>
        <button type="button" onClick={onClose} aria-label="Close editor" className="text-gray-400 hover:text-gray-600 cursor-pointer"><X size={16} /></button>
      </div>

      <div>
        <p className="mb-1 flex items-center gap-1 text-xs font-medium text-gray-700"><Crosshair size={12} /> Click the most important part of the picture</p>
        <div className="relative cursor-crosshair overflow-hidden rounded-md bg-gray-100" onClick={pick} role="presentation">
          <img src={websiteImageUrl(image.id, 960)} alt="" className="block w-full select-none" draggable={false} />
          <span
            className="pointer-events-none absolute h-5 w-5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white bg-indigo-600/70 shadow"
            style={{ left: `${focus.x}%`, top: `${focus.y}%` }}
            data-testid="focus-dot"
          />
        </div>
        <p className="mt-1 text-xs text-gray-500">When the picture is cropped to fit a card, this part stays in view.</p>
      </div>

      <div className="space-y-1">
        <label className="text-xs font-medium text-gray-700" htmlFor={`alt-${image.id}`}>Description</label>
        <Input id={`alt-${image.id}`} value={alt} maxLength={MAX_ALT} onChange={e => setAlt(e.target.value)} />
      </div>
      {error && <p className="text-xs text-red-600">{error}</p>}
      <div className="flex justify-end gap-2">
        <Button type="button" size="sm" variant="outline" onClick={onClose}>Cancel</Button>
        <Button type="button" size="sm" disabled={!alt.trim() || save.isPending} onClick={() => save.mutate()}>{save.isPending ? 'Saving…' : 'Save'}</Button>
      </div>
    </div>
  )
}

/** The course's picture gallery. The first picture is the main one. Changes here save straight away. */
export default function WebsiteImages({ courseId, images }: { courseId: string; images: WebsiteImageDto[] }) {
  const queryClient = useQueryClient()
  const [adding, setAdding] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null)
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['course-website', courseId] })

  const upload = useMutation({
    mutationFn: ({ file, alt }: { file: File; alt: string }) => adminApi.uploadCourseImage(courseId, file, alt),
    onSuccess: async () => { setAdding(false); setError(null); await refresh() },
    onError: err => setError(categoryErrorMessage(err, 'Could not upload that picture.')),
  })
  const reorder = useMutation({
    mutationFn: (ids: string[]) => adminApi.reorderCourseImages(courseId, ids),
    onSettled: refresh,
    onError: err => setError(categoryErrorMessage(err)),
  })
  const remove = useMutation({
    mutationFn: (id: string) => adminApi.deleteWebsiteImage(id),
    onSuccess: async () => { setConfirmDeleteId(null); await refresh() },
    onError: err => { setConfirmDeleteId(null); setError(categoryErrorMessage(err)) },
  })

  const makeMain = (id: string) => reorder.mutate([id, ...images.map(i => i.id).filter(x => x !== id)])
  const full = images.length >= MAX_IMAGES

  return (
    <div className="space-y-3">
      {images.length === 0 && !adding && (
        <p className="text-sm text-gray-500">No pictures yet. The course will appear without one, which is fine, but a good photo makes it stand out.</p>
      )}

      {images.length > 0 && (
        <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3" aria-label="Course pictures">
          {images.map((image, index) => (
            <li key={image.id} className="overflow-hidden rounded-lg border border-gray-200 bg-white">
              <div className="relative">
                <FocusedImage image={image} width={480} className="aspect-[4/3] w-full" />
                {index === 0 && (
                  <span className="absolute left-2 top-2 inline-flex items-center gap-1 rounded-full bg-indigo-600 px-2 py-0.5 text-[11px] font-medium text-white">
                    <Star size={10} /> Main
                  </span>
                )}
              </div>
              <div className="space-y-1.5 p-2">
                <p className="truncate text-xs text-gray-600" title={image.altText}>{image.altText}</p>
                {confirmDeleteId === image.id ? (
                  <div className="flex items-center gap-1.5">
                    <Button type="button" size="sm" variant="destructive" className="h-7 px-2 text-xs" onClick={() => remove.mutate(image.id)}>Delete</Button>
                    <Button type="button" size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => setConfirmDeleteId(null)}>Keep</Button>
                  </div>
                ) : (
                  <div className="flex items-center gap-1">
                    {index > 0 && (
                      <button type="button" onClick={() => makeMain(image.id)} disabled={reorder.isPending}
                        className="rounded px-1.5 py-1 text-xs text-gray-500 hover:bg-gray-100 hover:text-indigo-600 cursor-pointer">Make main</button>
                    )}
                    <button type="button" onClick={() => setEditingId(editingId === image.id ? null : image.id)} aria-label={`Edit ${image.altText}`}
                      className="ml-auto rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-indigo-600 cursor-pointer"><Pencil size={14} /></button>
                    <button type="button" onClick={() => setConfirmDeleteId(image.id)} aria-label={`Delete ${image.altText}`}
                      className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-red-600 cursor-pointer"><Trash2 size={14} /></button>
                  </div>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {editingId && images.find(i => i.id === editingId) && (
        <ImageEditor key={editingId} image={images.find(i => i.id === editingId)!} onClose={() => setEditingId(null)} />
      )}

      {adding ? (
        <ImageUploadForm
          pending={upload.isPending}
          error={error}
          onUpload={(file, alt) => upload.mutate({ file, alt })}
          onCancel={() => { setAdding(false); setError(null) }}
        />
      ) : (
        <div className="flex flex-wrap items-center gap-3">
          <Button type="button" size="sm" variant="outline" className="gap-2" disabled={full} onClick={() => { setAdding(true); setError(null) }}>
            <ImagePlus size={15} /> Add a picture
          </Button>
          <span className="text-xs text-gray-400">{images.length} of {MAX_IMAGES}{full ? ' (remove one to add another)' : ''}</span>
        </div>
      )}
      {!adding && error && <p className="text-xs text-red-600">{error}</p>}
    </div>
  )
}
